// BL-177 Step 2o (section 4): the in-app pop-up STACK, as a pure reducer so it can be tested without a browser.
//
// Before: one pop-up slot. Every new alert overwrote the one on screen, and when several arrived together only the first was shown
// (the rest were marked "already popped" and never seen). Now every alert that should pop up is kept:
//   - newest card on top, at most MAX_VISIBLE_POPUPS shown, the rest summarised as "+N more";
//   - each card stays until it is dismissed, opened, read elsewhere, or its own timeout runs out. A new message joining a card bumps the
//     card's `version`, which restarts that card's timer (the view keeps one timer per card id + version; the reducer reads no clock);
//   - several messages from the SAME conversation collapse into one card with a count ("3 new messages from Roger").
// The bell's unread count is separate (it counts every alert); nothing here removes an alert from the bell.
export const MAX_VISIBLE_POPUPS = 3;

export function emptyPopupStack() {
    return { cards: [], seq: 0 };
}

/** The conversation an alert belongs to, or the alert itself when it is not about a conversation (those never group). */
export function popupThreadKey(item) {
    const sessionId = typeof item?.payload?.sessionId === 'string' ? item.payload.sessionId.toLowerCase() : '';
    return sessionId ? `thread:${sessionId}` : `item:${item?.key || item?.id}`;
}

const byCreated = (a, b) => (Date.parse(a.createdAt || '') || 0) - (Date.parse(b.createdAt || '') || 0);

/**
 * actions:
 *   { type: 'add', items }          alerts that arrived (already filtered for "may pop up")
 *   { type: 'dismiss', id }         one card closed / opened / timed out
 *   { type: 'dismissAll' }
 *   { type: 'prune', unreadKeys }   drop alerts that have been read elsewhere (a Set of the keys still unread)
 */
export function popupStackReducer(state = emptyPopupStack(), action = {}) {
    switch (action.type) {
    case 'add': {
        let cards = state.cards;
        let seq = state.seq;
        const known = new Set(cards.flatMap((card) => card.items.map((entry) => entry.key)));
        for (const item of [...(action.items || [])].sort(byCreated)) {
            if (!item || known.has(item.key)) continue; // the same alert found twice (live path + timed check) is one alert
            known.add(item.key);
            seq += 1;
            const threadKey = popupThreadKey(item);
            const existing = cards.find((card) => card.threadKey === threadKey);
            const rest = cards.filter((card) => card !== existing);
            const items = existing ? [...existing.items, item] : [item];
            cards = [{ id: threadKey, threadKey, items, count: items.length, version: seq }, ...rest];
        }
        return seq === state.seq ? state : { cards, seq };
    }
    case 'dismiss':
        return state.cards.some((card) => card.id === action.id) ? { ...state, cards: state.cards.filter((card) => card.id !== action.id) } : state;
    case 'dismissAll':
        return state.cards.length ? { ...state, cards: [] } : state;
    case 'prune': {
        const unread = action.unreadKeys;
        let changed = false;
        const cards = [];
        for (const card of state.cards) {
            const items = card.items.filter((entry) => unread.has(entry.key));
            if (items.length !== card.items.length) changed = true;
            if (items.length) cards.push(items.length === card.items.length ? card : { ...card, items, count: items.length });
        }
        return changed ? { ...state, cards } : state;
    }
    default:
        return state;
    }
}

export const visibleCards = (state) => state.cards.slice(0, MAX_VISIBLE_POPUPS);
export const hiddenCardCount = (state) => Math.max(0, state.cards.length - MAX_VISIBLE_POPUPS);
/** Every alert still held in the stack, hidden cards included. */
export const heldAlertCount = (state) => state.cards.reduce((sum, card) => sum + card.count, 0);

/** The card's latest alert (what Open / Reply act on). */
export const cardLatest = (card) => card.items[card.items.length - 1];
